/**
 * LOKAL TID SOM ALTERNATIV TIL ET ISO-TIDSPUNKT (AI-ANALYSE §8.1 #8).
 *
 * Nettsiden gjør «18:00» om til et tidspunkt i årgangens sone før den sender (`wallClockToInstant`).
 * En AI-klient eller mobilappen vet ofte bare veggklokka — «lydsjekk 16:30 fredag». Rutene som er
 * merket med `@LocalTimes` i API-et godtar derfor `{ localDateTime, timeZone? }` i stedet for ISO i
 * tidsfeltene. Sonen er den som sendes, ellers årgangens, ellers organisasjonens. API-et gjør verdien
 * om til ISO før skjemaet leser kroppen, så resten av kontrakten er uendret.
 */
import { z } from 'zod';

import { wallClockToInstant } from '../zoned-time.js';

export const localDateTimeSchema = z
  .object({
    /** Veggklokka der arrangementet er: `2027-06-12T18:00` (sekunder valgfritt), uten sone. */
    localDateTime: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/, 'Expected YYYY-MM-DDTHH:mm[:ss].'),
    /** IANA-sone. Utelatt: årgangens sone, ellers organisasjonens. */
    timeZone: z.string().trim().min(1).max(64).optional(),
  })
  .meta({ id: 'LocalDateTime' });
export type LocalDateTime = z.infer<typeof localDateTimeSchema>;

/** Gyldig IANA-sone etter plattformens egen tabell. */
export function isTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch {
    return false;
  }
}

/** Veggklokka i sonen som ISO-tidspunkt, eller null når dagen eller klokka ikke finnes. */
export function localDateTimeToInstant(localDateTime: string, timeZone: string): string | null {
  const [date, clock] = localDateTime.split('T');
  if (date === undefined || clock === undefined) return null;
  const [hh, mm, ss] = clock.split(':');
  const instant = wallClockToInstant(date, (hh ?? '') + ':' + (mm ?? ''), timeZone);
  if (instant === null) return null;
  const seconds = ss === undefined ? 0 : Number(ss);
  if (!Number.isInteger(seconds) || seconds > 59) return null;
  return new Date(Date.parse(instant) + seconds * 1000).toISOString();
}
